import { useState } from 'react';
import { Table, Button, Pagination, Form, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiDocument, HiTrash, HiXMark, HiChevronUp, HiChevronDown, HiChevronUpDown } from 'react-icons/hi2';
import { useListDocumentsQuery, useDeleteDocumentMutation, useRetryDocumentMutation, useListLabelsQuery, DOCUMENT_POLL_INTERVAL_MS } from '@/store/documentsApi';
import { DocumentStatus } from '@/types';
import DocumentTableRow, { DocumentMobileCardItem } from './DocumentTableRow';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import useConfirm from '@/hooks/useConfirm';
import useTableSort from '@/hooks/useTableSort';
import useDocumentFilters from '@/hooks/useDocumentFilters';
import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  TextCaption,
  DesktopOnly,
  MobileOnly,
  MobileCardList,
  ToolbarRow,
  LabelFilterRow,
  LabelFilterCaption,
  FilterPill,
  SortableHeaderCell,
  CheckboxCell,
  FileIconCell,
  ActionsCell,
} from './DocumentList.styled';

type SortField = 'fileName' | 'status' | 'fileSizeBytes' | 'pageCount' | 'createdAt';

function SortIcon({ field, activeField, activeOrder }: { field: SortField; activeField?: string; activeOrder?: string }) {
  if (field !== activeField) return <HiChevronUpDown size="0.875rem" className="ms-1 text-muted opacity-50" />;
  return activeOrder === 'asc'
    ? <HiChevronUp size="0.875rem" className="ms-1" />
    : <HiChevronDown size="0.875rem" className="ms-1" />;
}

export default function DocumentList() {
  const { t } = useTranslation();
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const pageSize = 20;

  const {
    filters,
    queryArgs,
    setPage,
    setStatusFilter,
    toggleLabel,
    clearLabelFilters,
    handleSortChange,
  } = useDocumentFilters(pageSize);

  const { sortBy, sortOrder, handleSort } = useTableSort<SortField>({
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    onSortChange: handleSortChange,
  });

  const { data, isLoading, isFetching } = useListDocumentsQuery(queryArgs);
  const { data: labels } = useListLabelsQuery();

  const hasProcessing =
    data?.items?.some(
      (d) => d.status !== DocumentStatus.Ready && d.status !== DocumentStatus.Failed,
    ) ?? false;

  // Poll only while at least one document is still processing, so the list reflects backend
  // status transitions (parse → label → embed → ready) that happen out of band on Service Bus
  // workers. This second subscription shares the same cache entry as the display query above
  // (identical args), so it adds a polling timer without issuing a separate request. Once every
  // document is Ready/Failed, hasProcessing is false and the interval drops to 0 (polling off) —
  // no idle DB load. The query is paginated + filtered, so each poll is the same targeted page.
  useListDocumentsQuery(queryArgs, {
    pollingInterval: hasProcessing ? DOCUMENT_POLL_INTERVAL_MS : 0,
  });

  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
  const [retryDocument] = useRetryDocumentMutation();
  const { confirm, confirmProps } = useConfirm();

  const handleDelete = async (id: string, fileName: string) => {
    const confirmed = await confirm({
      title: t('documents.deleteTitle'),
      message: t('documents.deleteConfirm', { fileName }),
      variant: 'danger',
      confirmLabel: t('confirm.delete'),
    });
    if (confirmed) {
      await deleteDocument(id);
      setSelectedDocIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedDocIds.size;
    if (count === 0) return;
    const confirmed = await confirm({
      title: t('documents.bulkDeleteTitle'),
      message: t('documents.bulkDeleteConfirm', { count }),
      variant: 'danger',
      confirmLabel: t('confirm.delete'),
    });
    if (confirmed) {
      await Promise.all([...selectedDocIds].map((id) => deleteDocument(id)));
      setSelectedDocIds(new Set());
    }
  };

  const toggleDocSelection = (id: string) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!data?.items) return;
    const allIds = data.items.map((d) => d.id);
    const allSelected = allIds.every((id) => selectedDocIds.has(id));
    setSelectedDocIds(allSelected ? new Set() : new Set(allIds));
  };

  const handleRetry = (id: string) => {
    retryDocument(id);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const { page, statusFilter, selectedLabelIds } = filters;
  const totalPages = data ? Math.ceil(data.totalCount / pageSize) : 0;

  return (
    <div>
      <ToolbarRow>
        <Form.Select
          size="sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">{t('documents.allStatuses')}</option>
          {Object.values(DocumentStatus).map((s) => (
            <option key={s} value={s}>
              {t(`status.${s.toLowerCase()}`)}
            </option>
          ))}
        </Form.Select>
        {selectedDocIds.size > 0 && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleBulkDelete}
            disabled={isDeleting}
          >
            <HiTrash size="0.875rem" className="me-1" />
            {t('documents.bulkDelete', { count: selectedDocIds.size })}
          </Button>
        )}
        {isFetching && <LoadingSpinner size="sm" />}
      </ToolbarRow>

      {labels && labels.length > 0 && (
        <LabelFilterRow>
          <LabelFilterCaption>
            {t('documents.filterByLabel', 'Labels:')}
          </LabelFilterCaption>
          {labels.map((label) => (
            <FilterPill key={label.id} onClick={() => toggleLabel(label.id)}>
              <Badge
                bg={selectedLabelIds.includes(label.id) ? 'primary' : 'light'}
                text={selectedLabelIds.includes(label.id) ? 'white' : 'dark'}
                role="button"
                className="border"
              >
                {label.name}
                {selectedLabelIds.includes(label.id) && (
                  <HiXMark size="0.7rem" className="ms-1" />
                )}
              </Badge>
            </FilterPill>
          ))}
          {selectedLabelIds.length > 0 && (
            <FilterPill onClick={clearLabelFilters}>
              <Badge bg="outline-secondary" role="button" className="border text-muted">
                {t('documents.clearFilters', 'Clear')}
              </Badge>
            </FilterPill>
          )}
        </LabelFilterRow>
      )}

      {!data?.items?.length ? (
        <EmptyState>
          <EmptyStateIcon>
            <HiDocument />
          </EmptyStateIcon>
          <EmptyStateTitle>{t('documents.empty.title')}</EmptyStateTitle>
          <EmptyStateDescription>
            {t('documents.empty.description')}
          </EmptyStateDescription>
        </EmptyState>
      ) : (
        <>
          <DesktopOnly>
            <Table hover responsive className="align-middle">
              <thead>
                <tr>
                  <CheckboxCell>
                    <Form.Check
                      type="checkbox"
                      checked={!!data?.items?.length && data.items.every((d) => selectedDocIds.has(d.id))}
                      onChange={toggleSelectAll}
                    />
                  </CheckboxCell>
                  <FileIconCell />
                  {([
                    ['fileName', 'documents.table.name'],
                    ['status', 'documents.table.status'],
                    ['fileSizeBytes', 'documents.table.size'],
                    ['pageCount', 'documents.table.pages'],
                    ['createdAt', 'documents.table.uploaded'],
                  ] as [SortField, string][]).map(([field, labelKey]) => (
                    <SortableHeaderCell key={field} onClick={() => handleSort(field)}>
                      {t(labelKey)}
                      <SortIcon field={field} activeField={sortBy} activeOrder={sortOrder} />
                    </SortableHeaderCell>
                  ))}
                  <ActionsCell />
                </tr>
              </thead>
              <tbody>
                {data.items.map((doc) => (
                  <DocumentTableRow
                    key={doc.id}
                    doc={doc}
                    isSelected={selectedDocIds.has(doc.id)}
                    isDeleting={isDeleting}
                    onToggleSelect={toggleDocSelection}
                    onDelete={handleDelete}
                    onRetry={handleRetry}
                  />
                ))}
              </tbody>
            </Table>
          </DesktopOnly>

          <MobileOnly>
            <MobileCardList>
              {data.items.map((doc) => (
                <DocumentMobileCardItem
                  key={doc.id}
                  doc={doc}
                  isDeleting={isDeleting}
                  onDelete={handleDelete}
                  onRetry={handleRetry}
                />
              ))}
            </MobileCardList>
          </MobileOnly>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <TextCaption>
                {t('documents.showing', {
                  from: (page - 1) * pageSize + 1,
                  to: Math.min(page * pageSize, data.totalCount),
                  total: data.totalCount,
                })}
              </TextCaption>
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev disabled={page === 1} onClick={() => setPage((p) => p - 1)} />
                {(() => {
                  const maxVisible = 5;
                  let start = Math.max(1, page - Math.floor(maxVisible / 2));
                  const end = Math.min(totalPages, start + maxVisible - 1);
                  start = Math.max(1, end - maxVisible + 1);
                  return Array.from({ length: end - start + 1 }, (_, i) => {
                    const pageNum = start + i;
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === page}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  });
                })()}
                <Pagination.Next
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                />
              </Pagination>
            </div>
          )}
        </>
      )}
      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
